import React, {
  useContext,
  useRef,
  createRef,
  MutableRefObject,
  RefObject,
  useState,
  useEffect,
  ReactElement,
} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import DeviceContext, {Device} from '../../../RootProvider/DeviceContext';
import {getPageRoute} from '../../../utils/routes/getPageRoute';
import {useLocale} from '../../../RootProvider/LocaleContext';
import TrustpilotWidget from '../TrustpilotWidget/TrustpilotWidget';
import {ArrowIcon} from '../Icons';
import {LinkProp} from '../../../types';
import {IconSize} from '../Icons/IconWrapper';

import './JumpToAnchorMenu.scss';
import DynamicLink from '../../DynamicLink/DynamicLink';
import Button, {ButtonVariant} from '../Buttons/Button/Button';

interface Props {
  className?: string;
  customCtaText?: string;
  links: LinkProp[];
  shouldShowTrustpilot?: boolean;
  shouldShowQuoteFormCTA?: boolean;
}

function JumpToAnchorMenu({
  className,
  customCtaText,
  links,
  shouldShowQuoteFormCTA,
  shouldShowTrustpilot,
}: Props): ReactElement {
  const device = useContext(DeviceContext);
  const isHandheld = device === Device.MOBILE || device === Device.TABLET;
  const transitionTimeoutRef: MutableRefObject<number | undefined> = useRef();
  const contentElRef: RefObject<HTMLDivElement> = createRef();
  const [isContentOpen, setIsContentOpen] = useState(false);
  const locale = useLocale();
  const {formatMessage} = useIntl();

  /**
   * takes care of the closed -> open transition
   */
  useEffect(() => {
    const item: HTMLDivElement | undefined = contentElRef.current || undefined;
    if (!item || !isHandheld) {
      return () => {
        // noop
      };
    }
    clearTimeout(transitionTimeoutRef.current);
    if (isContentOpen) {
      // Using requestAnimationFrame makes sure that all commands happend before the browser paints
      requestAnimationFrame(() => {
        // Avoid animations so we make absolutely sure the user won't see anything happening
        item.style.transition = 'none';
        // Sets the height to auto so the browser opens the container to the desired size
        item.style.height = 'auto';
        // We grab the height information to use later
        const height = item.offsetHeight;
        // Set the container height back to 0 (remember, nothing was painted yet) to be ready for transitioning
        item.style.height = '0';
        // Reapply the transition
        item.style.transition = 'all 250ms';
        transitionTimeoutRef.current = window.setTimeout(() => {
          // Now that we're ready, we apply the height on the next paint. Otherwise there will be no animation, just a jump.
          item.style.height = `${height}px`;
        }, 0);
        // set it back to auto so when the content size changes, the container changes accordingly
        transitionTimeoutRef.current = window.setTimeout(() => {
          item.style.height = 'auto';
        }, 300);
      });
    } else {
      item.style.height = `${item.offsetHeight}px`;
      requestAnimationFrame(() => {
        item.style.height = '0';
      });
    }
    return () => {
      clearTimeout(transitionTimeoutRef.current);
    };
  }, [isContentOpen, isHandheld]);

  return (
    <div className={`jumpToAnchorMenu ${className || ''}`.trim()} data-testid="ui.jumpToAnchorMenu">
      {links.length > 0 ? (
        <>
          {/* Adds separation between title and content if it's a handheld device (not desktop) */}
          {isHandheld ? (
            <div
              className="jumpToAnchorMenu__title"
              data-testid="ui.jumpToAnchorMenu.title"
              onClick={() => {
                setIsContentOpen(!isContentOpen);
              }}
              onKeyUp={() => null}
              role="switch"
              tabIndex={0}
              aria-checked
            >
              <div className="paddingless-xs-only">
                <div className="row">
                  <div className="col-xs">
                    <FormattedMessage id="phoenix.jumpToAnchorMenu.title" />
                  </div>
                  <div className="col-xs-narrow">
                    <ArrowIcon
                      size={IconSize.LARGE}
                      className={`jumpToAnchorMenu__title__arrow ${
                        isContentOpen ? 'jumpToAnchorMenu__title__arrow--open' : ''
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="jumpToAnchorMenu__title">
              <FormattedMessage id="phoenix.jumpToAnchorMenu.title" />
            </div>
          )}
          <div
            className={`jumpToAnchorMenu__contents ${
              isContentOpen || !isHandheld ? 'jumpToAnchorMenu__contents--open' : ''
            }`}
            ref={contentElRef}
            data-testid="ui.jumpToAnchorMenu.contents"
          >
            <div className="container paddingless jumpToAnchorMenu__contents__container">
              <ul>
                {links.map((entry) => (
                  <li key={entry.label}>
                    <DynamicLink
                      href={`#${entry.href}`}
                      className="unstyledLink"
                      onClick={() => setIsContentOpen(false)}
                    >
                      {entry.label}
                    </DynamicLink>
                  </li>
                ))}
              </ul>
              {shouldShowQuoteFormCTA && (
                <Button
                  href={getPageRoute('quoteForm.start', locale)}
                  variant={ButtonVariant.CALL_TO_ACTION}
                  ariaLabel={customCtaText || formatMessage({id: 'phoenix.knowledge.workingCapital.hero.button'})}
                >
                  {customCtaText || <FormattedMessage id="phoenix.knowledge.workingCapital.hero.button" />}
                </Button>
              )}
              {shouldShowTrustpilot && <TrustpilotWidget />}
            </div>
          </div>
        </>
      ) : (
        <div className="jumpToAnchorMenu__contents jumpToAnchorMenu__contents--open">
          {shouldShowQuoteFormCTA && (
            <Button
              href={getPageRoute('quoteForm.start', locale)}
              variant={ButtonVariant.CALL_TO_ACTION}
              ariaLabel={customCtaText || formatMessage({id: 'phoenix.knowledge.workingCapital.hero.button'})}
            >
              {customCtaText || <FormattedMessage id="phoenix.knowledge.workingCapital.hero.button" />}
            </Button>
          )}
          {shouldShowTrustpilot && <TrustpilotWidget />}
        </div>
      )}
    </div>
  );
}

export default JumpToAnchorMenu;
